'use client'

import React, {
  useCallback,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react'
import { useTranslations } from 'next-intl'
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowClockwise'
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowCounterClockwise'
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr/DotsThreeVertical'
import { EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye'
import { SquareSplitHorizontalIcon } from '@phosphor-icons/react/dist/ssr/SquareSplitHorizontal'
import { Button, DropdownMenu } from '@skop-app/lib-front'
import { cx } from 'class-variance-authority'

import { useGridApi, useGridViews } from './GridRoot'
import { GRID_TOOLBAR_CONTROL_CLASSNAME } from './toolbarControlStyles'
import { usePreventGridFocusSteal } from './usePreventGridFocusSteal'
import { GridViewMenuItems } from './GridViewButton'
import type { GridToolbarOverflowProps } from '../types'

const NOOP_UNSUBSCRIBE = () => {}
const UNDO_REDO_EVENTS = [
  'cellValueChanged',
  'cellEditingStopped',
  'undoEnded',
  'redoEnded',
  'modelUpdated',
  'pasteEnd',
  'fillEnd',
  'batchEditingStopped',
  'bulkEditingStopped',
] as const

/**
 * Overflow menu for toolbar actions that don't fit at narrow widths.
 *
 * The trigger button is hidden at `lg` and above (`lg:hidden`).
 * Pair with `hidden lg:flex` on the full-size toolbar items to swap between
 * inline and overflow display.
 */
function GridToolbarOverflowRoot({
  children,
  className,
  align = 'end',
}: GridToolbarOverflowProps) {
  const preventFocusSteal = usePreventGridFocusSteal()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="action"
          className={cx(GRID_TOOLBAR_CONTROL_CLASSNAME, 'lg:hidden')}
          onMouseDown={preventFocusSteal}
        >
          <DotsThreeVerticalIcon className="size-5" weight="bold" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align={align}
        className={cx('min-w-[180px]', className)}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

/** Dropdown items replicating `Grid.UndoRedo` inside the overflow menu. */
function UndoRedoItems({ disabled }: { disabled?: boolean }) {
  const t = useTranslations()
  const { api, gridIsReady } = useGridApi()
  const views = useGridViews()
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!api || !gridIsReady) return NOOP_UNSUBSCRIBE

      for (const eventName of UNDO_REDO_EVENTS) {
        api.addEventListener(eventName, onStoreChange)
      }

      return () => {
        try {
          for (const eventName of UNDO_REDO_EVENTS) {
            api.removeEventListener(eventName, onStoreChange)
          }
        } catch {
          // grid may be torn down during fast navigation
        }
      }
    },
    [api, gridIsReady],
  )
  const getCanUndo = useCallback(
    () => (api && gridIsReady ? api.getCurrentUndoSize() > 0 : false),
    [api, gridIsReady],
  )
  const getCanRedo = useCallback(
    () => (api && gridIsReady ? api.getCurrentRedoSize() > 0 : false),
    [api, gridIsReady],
  )
  const handleUndo = useCallback(() => {
    api?.undoCellEditing()
  }, [api])
  const handleRedo = useCallback(() => {
    api?.redoCellEditing()
  }, [api])
  const canUndo = useSyncExternalStore(subscribe, getCanUndo, () => false)
  const canRedo = useSyncExternalStore(subscribe, getCanRedo, () => false)

  const isDisabled =
    views?.isInteractionBlocked === true || (disabled ?? !gridIsReady)

  return (
    <>
      <DropdownMenu.Item onClick={handleUndo} disabled={isDisabled || !canUndo}>
        <ArrowCounterClockwiseIcon className="mr-2 size-4" />
        <span>{t('Toolbar.undo')}</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={handleRedo} disabled={isDisabled || !canRedo}>
        <ArrowClockwiseIcon className="mr-2 size-4" />
        <span>{t('Toolbar.redo')}</span>
      </DropdownMenu.Item>
    </>
  )
}

/** Dropdown item replicating `Grid.ColumnsButton` inside the overflow menu. */
function ColumnsItem({ disabled }: { disabled?: boolean }) {
  const t = useTranslations()
  const { api, gridIsReady, requestColumnsPanel } = useGridApi()
  const views = useGridViews()

  useLayoutEffect(() => {
    requestColumnsPanel()
  }, [requestColumnsPanel])

  const handleClick = useCallback(() => {
    if (!api || !gridIsReady || views?.isInteractionBlocked === true) return
    const isVisible = api.isSideBarVisible()
    api.setSideBarVisible(!isVisible)
    if (!isVisible) {
      api.openToolPanel('columns')
    }
  }, [api, gridIsReady, views?.isInteractionBlocked])

  const isDisabled =
    views?.isInteractionBlocked === true || (disabled ?? !gridIsReady)

  return (
    <DropdownMenu.Item onClick={handleClick} disabled={isDisabled}>
      <SquareSplitHorizontalIcon className="mr-2 size-4" />
      <span>{t('Toolbar.columns')}</span>
    </DropdownMenu.Item>
  )
}

/** Overflow sub-menu replicating `Grid.ViewButton` when views are enabled. */
function ViewItem({ disabled }: { disabled?: boolean }) {
  const t = useTranslations()
  const views = useGridViews()

  if (!views?.enabled) return null

  const isDisabled = disabled ?? (!views.enabled || views.isLoading)

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger disabled={isDisabled}>
        <span
          className="contents"
          onMouseDown={(event) => event.preventDefault()}
        >
          <EyeIcon className="mr-2 size-4" />
          <span>{t('Toolbar.view')}</span>
        </span>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="min-w-[260px] bg-white">
          <GridViewMenuItems />
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  )
}

export const GridToolbarOverflow = Object.assign(GridToolbarOverflowRoot, {
  UndoRedoItems,
  ColumnsItem,
  ViewItem,
})
